import { createModalFocusScope } from '../components/modal-focus-scope.js';

const FALLBACK_AMBIENT_HSL = { h: 0, s: 72, l: 40 };
const STORAGE_KEY_PREFIX = 'game-app-tracker:game-details-ambient:';
const COLOR_SAMPLE_SIZE = 8;

const pendingAmbientBySource = new Map();

(function () {
    initGameDetailsAmbient();
    initGameDeleteModal();
})();

function initGameDetailsAmbient() {
    const rootElement = document.querySelector('[data-game-details-ambient-root]');
    if (!(rootElement instanceof HTMLElement)) {
        return;
    }

    const coverImage = rootElement.querySelector('[data-game-details-cover-image]');
    if (!(coverImage instanceof HTMLImageElement)) {
        applyAmbient(rootElement, FALLBACK_AMBIENT_HSL);

        return;
    }

    const applyFallback = () => {
        applyAmbient(rootElement, FALLBACK_AMBIENT_HSL);
    };

    const applyCoverAmbient = () => {
        resolveAmbientForImage(coverImage)
            .then((ambient) => applyAmbient(rootElement, ambient))
            .catch(() => applyFallback());
    };

    if (coverImage.complete) {
        if (coverImage.naturalWidth > 0 && coverImage.naturalHeight > 0) {
            applyCoverAmbient();

            return;
        }

        applyFallback();

        return;
    }

    coverImage.addEventListener('load', applyCoverAmbient, { once: true });
    coverImage.addEventListener('error', applyFallback, { once: true });
}

function initGameDeleteModal() {
    const modal = document.querySelector('[data-game-delete-modal]');
    if (!(modal instanceof HTMLElement)) {
        return;
    }

    const openButton = document.querySelector('[data-game-delete-open]');
    if (!(openButton instanceof HTMLElement)) {
        return;
    }

    const dialog = modal.querySelector('[data-game-delete-dialog]');
    if (!(dialog instanceof HTMLElement)) {
        return;
    }

    const closeModal = () => {
        if (modal.hidden) {
            return;
        }

        modal.hidden = true;
        focusScope.deactivate();
    };
    const focusScope = createModalFocusScope({
        dialog,
        requestClose: closeModal,
    });
    if (focusScope === null) {
        return;
    }

    const openModal = () => {
        modal.hidden = false;
        focusScope.activate(openButton);
        window.requestAnimationFrame(() => focusScope.focus());
    };

    openButton.addEventListener('click', openModal);
    const closeButtons = modal.querySelectorAll('[data-game-delete-close]');
    closeButtons.forEach((button) => {
        button.addEventListener('click', closeModal);
    });
}

function resolveAmbientForImage(image) {
    const source = normalizeSourceUrl(image.currentSrc || image.src);
    if (source === '') {
        return Promise.resolve(FALLBACK_AMBIENT_HSL);
    }

    const cachedAmbient = readCachedAmbient(source);
    if (cachedAmbient !== null) {
        return Promise.resolve(cachedAmbient);
    }

    const pending = pendingAmbientBySource.get(source);
    if (pending !== undefined) {
        return pending;
    }

    const pendingAmbient = Promise.resolve()
        .then(() => extractAmbientFromImage(image))
        .catch(() => FALLBACK_AMBIENT_HSL)
        .then((ambient) => {
            const normalized = normalizeAmbient(ambient);
            cacheAmbient(source, normalized);

            return normalized;
        })
        .finally(() => {
            pendingAmbientBySource.delete(source);
        });

    pendingAmbientBySource.set(source, pendingAmbient);

    return pendingAmbient;
}

function extractAmbientFromImage(image) {
    const canvas = document.createElement('canvas');
    canvas.width = COLOR_SAMPLE_SIZE;
    canvas.height = COLOR_SAMPLE_SIZE;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) {
        return FALLBACK_AMBIENT_HSL;
    }

    try {
        context.drawImage(image, 0, 0, COLOR_SAMPLE_SIZE, COLOR_SAMPLE_SIZE);
        const pixelData = context.getImageData(0, 0, COLOR_SAMPLE_SIZE, COLOR_SAMPLE_SIZE).data;
        let redTotal = 0;
        let greenTotal = 0;
        let blueTotal = 0;
        let pixelCount = 0;

        for (let index = 0; index < pixelData.length; index += 4) {
            const red = pixelData[index];
            const green = pixelData[index + 1];
            const blue = pixelData[index + 2];
            const alpha = pixelData[index + 3];

            if (alpha < 64) {
                continue;
            }

            const brightness = (red + green + blue) / 3;
            if (brightness <= 24 || brightness >= 232) {
                continue;
            }

            redTotal += red;
            greenTotal += green;
            blueTotal += blue;
            pixelCount += 1;
        }

        if (pixelCount === 0) {
            return FALLBACK_AMBIENT_HSL;
        }

        const dominantRed = Math.round(redTotal / pixelCount);
        const dominantGreen = Math.round(greenTotal / pixelCount);
        const dominantBlue = Math.round(blueTotal / pixelCount);

        return rgbToHsl(dominantRed, dominantGreen, dominantBlue);
    } catch (_error) {
        return FALLBACK_AMBIENT_HSL;
    }
}

function applyAmbient(rootElement, ambient) {
    const normalized = normalizeAmbient(ambient);

    rootElement.style.setProperty('--ambient-h', String(normalized.h));
    rootElement.style.setProperty('--ambient-s', `${normalized.s}%`);
    rootElement.style.setProperty('--ambient-l', `${normalized.l}%`);
}

function rgbToHsl(red, green, blue) {
    const redNormalized = red / 255;
    const greenNormalized = green / 255;
    const blueNormalized = blue / 255;
    const max = Math.max(redNormalized, greenNormalized, blueNormalized);
    const min = Math.min(redNormalized, greenNormalized, blueNormalized);
    const delta = max - min;

    let hue = 0;
    if (delta !== 0) {
        if (max === redNormalized) {
            hue = ((greenNormalized - blueNormalized) / delta) % 6;
        } else if (max === greenNormalized) {
            hue = (blueNormalized - redNormalized) / delta + 2;
        } else {
            hue = (redNormalized - greenNormalized) / delta + 4;
        }
    }

    const hueDegrees = Math.round((hue * 60 + 360) % 360);
    const lightness = (max + min) / 2;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

    return {
        h: hueDegrees,
        s: Math.round(saturation * 100),
        l: Math.round(lightness * 100),
    };
}

function normalizeAmbient(value) {
    if (typeof value !== 'object' || value === null) {
        return { ...FALLBACK_AMBIENT_HSL };
    }

    const hue = Number.parseInt(String(value.h), 10);
    const saturation = Number.parseInt(String(value.s), 10);
    const lightness = Number.parseInt(String(value.l), 10);

    if ([hue, saturation, lightness].some((channel) => Number.isNaN(channel))) {
        return { ...FALLBACK_AMBIENT_HSL };
    }

    const normalizedHue = ((hue % 360) + 360) % 360;

    return {
        h: normalizedHue,
        s: clamp(saturation, 0, 95),
        l: clamp(lightness, 18, 62),
    };
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function readCachedAmbient(source) {
    try {
        const cached = window.localStorage.getItem(storageKey(source));
        if (cached === null) {
            return null;
        }

        return parseAmbientCache(cached);
    } catch (_error) {
        return null;
    }
}

function cacheAmbient(source, ambient) {
    try {
        const normalized = normalizeAmbient(ambient);
        window.localStorage.setItem(storageKey(source), `${normalized.h},${normalized.s},${normalized.l}`);
    } catch (_error) {
        // localStorage can be unavailable (privacy mode/quota); silently continue.
    }
}

function parseAmbientCache(value) {
    const match = String(value).match(/^\s*(-?\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*$/);
    if (match === null) {
        return null;
    }

    return normalizeAmbient({
        h: Number.parseInt(match[1], 10),
        s: Number.parseInt(match[2], 10),
        l: Number.parseInt(match[3], 10),
    });
}

function storageKey(source) {
    return `${STORAGE_KEY_PREFIX}${encodeURIComponent(source)}`;
}

function normalizeSourceUrl(value) {
    const source = String(value).trim();
    if (source === '') {
        return '';
    }

    return source;
}
